import React from 'react';
import { Modal, Button, Alert, ListGroup, Badge } from 'react-bootstrap';

const sanitizarSerie = (s) => String(s || '')
    .replace(/[\r\n\t]/g, ' ')
    .replace(/[<>&"']/g, '')
    .slice(0, 60);

/**
 * ModalConfirmarAccionPareja — modal genérico que pregunta si una acción sobre
 * un equipo en OT (activar, aplazar, desaprobar, etc.) debe aplicarse también
 * a su pareja (padre + hermanos) dentro de la misma OT.
 *
 * Spec: openspec/changes/items-adicionales-equipo-dependiente/specs/equipos-dependientes-sincronizacion-cancelacion.
 *
 * Props:
 *   show              boolean
 *   accionLabel       string — texto para el título (ej. "aplazamiento", "activación", "desaprobación")
 *   parejas           Array<{ equipoInOtSolicitado, parejaEnOt: [] }>
 *   onAplicarAmbos    (equiposParejaIds: string[]) => void
 *   onSoloEste        () => void
 *   onNoHacerNada     () => void
 */
const ModalConfirmarAccionPareja = ({
    show,
    accionLabel = 'acción',
    parejas = [],
    onAplicarAmbos,
    onSoloEste,
    onNoHacerNada,
}) => {

    const totalPareja = parejas.reduce((n, p) => n + (p.parejaEnOt?.length || 0), 0);

    const handleAplicarAmbos = () => {
        // Devolvemos los EquipmentInOts COMPLETOS (con snapshot, equipo, estado…) para que
        // el hook envolvente pueda pasarlos directo al handler sin depender de un lookup en
        // `equiposOtIni` (la pareja puede estar en otra hoja de trabajo/OT hija).
        const equiposPareja = parejas.flatMap(p => p.parejaEnOt || []);
        onAplicarAmbos(equiposPareja);
    };

    return (
        <Modal show={show} onHide={onNoHacerNada} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton={false}>
                <Modal.Title>Confirmar {accionLabel} con pareja</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    El equipo tiene <strong>{totalPareja} pareja(s)</strong> en esta OT.
                    ¿Quieres aplicar la {accionLabel} también a su pareja para mantener el grupo coherente?
                </Alert>

                {parejas.map((p, idx) => (
                    <div key={p.equipoInOtSolicitado?._id || idx} className="mb-3">
                        <div>
                            <strong>Aplicando {accionLabel} a:</strong>{' '}
                            {p.equipoInOtSolicitado?.snapshot?.item || p.equipoInOtSolicitado?.equipo?.item?.nombre}
                            {' — Serie '}
                            {sanitizarSerie(p.equipoInOtSolicitado?.snapshot?.serial || p.equipoInOtSolicitado?.equipo?.serie)}
                        </div>
                        <div className="ms-3 mt-2">
                            <strong>Pareja en esta OT:</strong>
                            <ListGroup className="mt-1">
                                {(p.parejaEnOt || []).map(pj => (
                                    <ListGroup.Item key={pj._id}>
                                        <Badge bg="info" className="me-2">🔗</Badge>
                                        {pj.snapshot?.item || pj.equipo?.item?.nombre || 'Item'}
                                        {' — Serie '}
                                        {sanitizarSerie(pj.snapshot?.serial || pj.equipo?.serie)}
                                        {pj.estado && <Badge bg="secondary" className="ms-2">{pj.estado}</Badge>}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onNoHacerNada}>
                    No hacer nada
                </Button>
                <Button variant="outline-warning" onClick={onSoloEste}>
                    Solo este equipo
                </Button>
                <Button variant="primary" onClick={handleAplicarAmbos} autoFocus>
                    Aplicar a ambos (recomendado)
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalConfirmarAccionPareja;
